/**
 * The trainee's own key names for the coach (ROUND7 §5): whatever he has bound, in the scheme he plays, printed
 * the way the HUD and the Controls screen print keys (`keyLabel`). Read live, so a rebind in the pause menu
 * shows in the next line the coach writes.
 */

import { type ActionId, type Bindings, keyLabel } from '../../input/bindings';
import type { MissionSettings } from '../missionSettings';

export interface KeyNames {
  /** `[W]`, `[LMB]`, `[SHIFT]` - the first key bound to the action, or `[-]` when nothing is. */
  key(action: ActionId): string;
  /** Held or toggled (Options): "HOLD [SHIFT]" or "PRESS [C]". */
  runToggle: boolean;
  crawlToggle: boolean;
  sneakToggle: boolean;
  /** The 1987 scheme: 8-way movement, and he aims the way he faces. */
  classic: boolean;
}

export type Toggles = Pick<MissionSettings, 'runToggle' | 'crawlToggle' | 'sneakToggle'>;

export function keyNames(bindings: Bindings, settings: Toggles): KeyNames {
  return {
    key: (action) => {
      const code = bindings.keysFor(action)[0];
      return `[${code === undefined ? '-' : keyLabel(code)}]`;
    },
    runToggle: settings.runToggle,
    crawlToggle: settings.crawlToggle,
    sneakToggle: settings.sneakToggle,
    get classic() {
      return bindings.scheme() === 'classic';
    },
  };
}

/** "HOLD [SHIFT]" / "PRESS [SHIFT]" for a held-or-toggled action. */
export function holdOrPress(keys: KeyNames, action: 'run' | 'crawl' | 'sneak'): string {
  const toggle = action === 'run' ? keys.runToggle : action === 'crawl' ? keys.crawlToggle : keys.sneakToggle;
  return `${toggle ? 'PRESS' : 'HOLD'} ${keys.key(action)}`;
}
